import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

const CLOSE_MS = 260
const modalStack: symbol[] = []

const ModalCloseContext = createContext<() => void>(() => {})

/** Call from inside a <Modal> to close it with its exit animation. */
export function useModalClose() {
  return useContext(ModalCloseContext)
}

interface ModalProps {
  onClose: () => void
  label: string
  children: ReactNode
  /** Optional accent glow colour at the top of the sheet. */
  glow?: string
  maxWidth?: number
}

/**
 * Glass modal. Springs in as a centred card on ≥640px and as a bottom sheet
 * on phones. Escape, the scrim and useModalClose() all play the exit
 * animation before calling onClose.
 */
export function Modal({
  onClose,
  label,
  children,
  glow,
  maxWidth = 600,
}: ModalProps) {
  const [closing, setClosing] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const closingRef = useRef(false)

  const close = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setClosing(true)
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches
    setTimeout(() => onCloseRef.current(), reduced ? 0 : CLOSE_MS)
  }, [])

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null
    const card = cardRef.current
    // Respect an element that already took focus via autoFocus.
    if (card && !card.contains(document.activeElement)) {
      const focusable = card.querySelector<HTMLElement>(
        'input, textarea, select, button:not([data-modal-close])',
      )
      ;(focusable ?? card).focus()
    }

    const me = Symbol('modal')
    modalStack.push(me)
    const onKey = (e: KeyboardEvent) => {
      // Only the top-most modal reacts to Escape.
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === me) {
        e.stopPropagation()
        close()
      }
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      modalStack.splice(modalStack.indexOf(me), 1)
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      prevFocus?.focus?.()
    }
  }, [close])

  return createPortal(
    <ModalCloseContext.Provider value={close}>
      <div className={`modal-wrap ${closing ? 'is-closing' : ''}`}>
        <button
          type="button"
          className="modal-scrim"
          aria-label="Close"
          tabIndex={-1}
          data-modal-close
          onClick={close}
        />
        <div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          tabIndex={-1}
          className="modal-card outline-none"
          style={{
            maxWidth,
            ...(glow ? ({ '--c1': glow } as React.CSSProperties) : {}),
          }}
        >
          {glow && <span className="modal-glow" aria-hidden="true" />}
          <span className="modal-grabber" aria-hidden="true" />
          <div className="relative">{children}</div>
        </div>
      </div>
    </ModalCloseContext.Provider>,
    document.body,
  )
}
